'use client';

import { Search, X } from 'lucide-react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import { useCallback, useEffect, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import useProducts from '../../app/api/dialogApis/searchProducts';
import useStores from '../../app/api/dialogApis/searchStores';
import { useTranslations } from 'next-intl';
import { Link, useRouter } from '@/i18n/routing';
import { Routes } from '@/lib/routes/routes';
import { useSearchStore } from '@/lib/state-stores/searchStore';
import { cn } from '@/lib/utils/command';
import CurrentLanguageText from '../helper/CurrentLanguageText';

type SearchDialog = {
  setOpen?: (e: boolean) => void;
  open?: boolean;
  searchValue?: string;
  list?: string[];
  onSearchValueChange?: (e?: string) => void;
  defaultLatitude: string;
  defaultLongitude: string;
  isLoading?: boolean;
  className?: string;
  dialogClassName?: string;
  mediaClass?: string;
  lang: 'ar' | 'en';
  isSotresPage: boolean;
};

const SearchHistory = ({ setOpen }: { setOpen: (e: boolean) => void }) => {
  const t = useTranslations('dialog');
  const { clearSearchHistory, removeSearch, searchHistory } = useSearchStore();
  const router = useRouter();

  return (
    <div className="shadow-pik mx-4 mt-4 overflow-y-auto rounded-xl p-4">
      {/* the heading  */}
      <div className="mx-2 mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold">{t('PreviousSearch')}</h2>
        <button
          onClick={clearSearchHistory}
          className="cursor-pointer transition-all duration-300 hover:tracking-wide"
        >
          {t('ClearAll')}
        </button>
      </div>

      {/* render list  of history search */}

      <div className="flex flex-col">
        {searchHistory.map((search, index) => (
          <div
            key={index}
            className={cn(
              'mx-4 flex cursor-pointer items-center justify-between py-2',
              index !== searchHistory.length - 1 && 'border-secondary border-b',
            )}
          >
            <button
              onClick={() => {
                router.push({
                  pathname: Routes.search,
                  query: {
                    query: search,
                  },
                });
                setOpen(false);
              }}
              className="w-full cursor-pointer text-start capitalize"
            >
              {search}
            </button>
            <button
              onClick={() => {
                removeSearch(search);
              }}
              className="cursor-pointer p-4"
            >
              <X className="h-4 w-4 text-gray-400" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const SearchComponent = (props: SearchDialog) => {
  const t = useTranslations('dialog');
  const [search, setSearch] = useState<string | undefined>();
  const [result, setResult] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [debouncedSearchVal, setDebouncedSearchVal] = useState<string | undefined>();
  const { addSearch } = useSearchStore();
  const router = useRouter();

  const {
    data: products,
    isFetched: isProductFetched,
    isPending,
    isFetching: isProductFetching,
    isError: isProductError,
  } = useProducts(debouncedSearchVal ?? '', props.isSotresPage);

  const {
    data: stores,
    isFetched: isStoresFetched,
    isPending: isPendingStores,
    isFetching: isStoresFetching,
    isError: isStoresError,
  } = useStores(
    debouncedSearchVal ?? '',
    props.defaultLatitude,
    props.defaultLongitude,
    props.isSotresPage,
  );

  useEffect(() => {
    if (isProductFetched || isStoresFetched) {
      const combinedResults = [...(products ?? []), ...(stores ?? [])];
      setResult(combinedResults);
    }
  }, [products, stores, isProductFetched, isStoresFetched]);

  const isLoading = isPending || isPendingStores;
  const isError = isProductError || isStoresError;
  const isFetching = isProductFetching || isStoresFetching;

  useHotkeys('mod+/', () => setOpen(!open), [open]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchVal(search);
    }, 500);
    return () => {
      clearTimeout(handler);
    };
  }, [search]);

  useEffect(() => {
    if (debouncedSearchVal) {
      addSearch(debouncedSearchVal);
    }
  }, [debouncedSearchVal, addSearch]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && debouncedSearchVal) {
      router.push({
        pathname: Routes.search,
        query: {
          query: debouncedSearchVal,
        },
      });
      setOpen(false);
    }
  };

  const renderContent = useCallback(() => {
    if (isLoading && isFetching) {
      return (
        <div className="grid h-42 place-items-center">
          <div className="border-primary h-8 w-8 animate-spin rounded-full border-b-2"></div>
        </div>
      );
    }

    if (isError) return <CommandEmpty>{t('no-result-found')}</CommandEmpty>;

    if (!debouncedSearchVal) {
      return <SearchHistory setOpen={setOpen} />;
    }

    if (result.length === 0) {
      return <CommandEmpty>{t('no-result-found')}</CommandEmpty>;
    }

    if (props.isSotresPage && stores?.length === 0) {
      return <CommandEmpty>{t('no-result-found')}</CommandEmpty>;
    }

    if (!props.isSotresPage && stores?.length === 0 && products?.length === 0) {
      return <CommandEmpty>{t('no-result-found')}</CommandEmpty>;
    }

    return (
      <CommandGroup className="styled-scroll space-y-2 overflow-y-auto">
        {result.map((item) => (
          <Link
            href={
              item.integrationId
                ? Routes.productDetails(item.branchId, item.integrationId)
                : Routes.branchDetails(item.id)
            }
            onClick={() => setOpen(false)}
            key={`search-result-${item.integrationId ?? item.id}`}
          >
            <CommandItem
              dir={props.lang === 'ar' ? 'rtl' : 'ltr'}
              className="border-secondary text-primary flex w-full items-center gap-2 truncate border-b text-ellipsis opacity-100 hover:cursor-pointer"
            >
              <Search className="text-gray-200" />
              <p className="">
                {!item.integrationId && (
                  <span className="mx-1 font-bold">
                    <CurrentLanguageText
                      arabicName={item.store?.arabicName}
                      englishName={item.store?.englishName}
                    />
                  </span>
                )}

                <span>
                  <CurrentLanguageText
                    arabicName={item.arabicName}
                    englishName={item.englishName}
                  />
                </span>
              </p>
            </CommandItem>
          </Link>
        ))}
      </CommandGroup>
    );
  }, [
    isLoading,
    isFetching,
    isError,
    t,
    result,
    props.lang,
    products?.length,
    props.isSotresPage,
    stores?.length,
    debouncedSearchVal,
  ]);

  return (
    <>
      <button
        className={cn(
          'group shadow-search hover:text-primary hover:outline-primary h-12 w-full flex-row items-center justify-between space-x-2 rounded-xl bg-transparent bg-white text-[#868687] outline outline-transparent transition-opacity hover:shadow-lg md:px-3 rtl:space-x-reverse',
          props.className,
        )}
        onClick={() => setOpen(true)}
      >
        <div className="flex flex-row items-center justify-start space-x-2 rtl:space-x-reverse">
          <Search size={20} />
          <p className="mt-1">{props.isSotresPage ? t('SearchForStores') : t('placeholder')}</p>
        </div>
        <kbd className="bg-secondary text-gray group-hover:bg-primary inline-flex h-8 items-center rounded-lg p-3 font-sans text-sm font-medium group-hover:text-white">
          /
        </kbd>
      </button>

      <CommandDialog
        open={open}
        onOpenChange={setOpen}
        className={props.dialogClassName}
        mediaClass={props.mediaClass}
      >
        <div className="m-4 mt-10 flex items-center justify-between overflow-hidden rounded-2xl border">
          <CommandInput
            value={search}
            onValueChange={setSearch}
            placeholder={props.isSotresPage ? t('SearchForStores') : t('placeholder')}
            dir={props.lang === 'ar' ? 'rtl' : 'ltr'}
            className="rtl:flex-row-reverse"
            onKeyDown={handleKeyDown}
          />
          <button
            className="bg-primary h-14 w-fit shrink-0 cursor-pointer px-6 text-white"
            onClick={() => {
              if (!debouncedSearchVal) return;
              router.push({ pathname: Routes.search, query: { query: debouncedSearchVal } });
              setOpen(false);
            }}
          >
            {t('Go')}
          </button>
        </div>

        <CommandList className="styled-scroll overflow-y-auto">{renderContent()}</CommandList>
      </CommandDialog>
    </>
  );
};

export const SearchDialog = (props: SearchDialog) => {
  return <SearchComponent {...props} />;
};
